import { sanitizePublicProductTruth } from "@smart-shikka/core";
import {
  BetaReadinessPanel,
  ByokProvidersPanel,
  ChatHistoryPanel,
  ConnectionsPanel,
  CostUsagePanel,
  DomainsPanel,
  GlobalRulesPanel,
  KnowledgeLibraryPanel,
  McpToolsPanel,
  MemoryPanel,
  SkillsSettings,
  VoiceSettingsPanel,
  createWorkspaceAgentSkillsService,
  ProductSettingsSurface,
  SafeErrorNotice,
  type ProductSettingsCapability,
  type ProductTruthEntry,
  type SettingsSection,
} from "@smart-shikka/react";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth.js";
import { AppNav } from "../components/AppNav.js";
import { SettingsVersionDetails } from "../components/VersionUpdateNotice.js";
import { useLocale, useTheme } from "../ui.js";

export function SettingsPage({
  initialSection = "general",
}: {
  initialSection?: SettingsSection;
}): React.JSX.Element {
  const { token, account } = useAuth();
  const { theme, setTheme } = useTheme();
  const { locale, setLocale } = useLocale();
  const superAdmin = account?.role === "super-admin";
  const skillsService = useMemo(
    () => createWorkspaceAgentSkillsService({
      baseUrl: "",
      getToken: () => token ?? undefined,
      getProjectContext: () => undefined,
    }),
    [token],
  );

  // Canonical product truth from GET /api/settings — capability states come
  // from ProductRuntimeTruth, never a static UI claim.
  const [truthEntries, setTruthEntries] = useState<readonly ProductTruthEntry[] | undefined>();
  const [truthFailed, setTruthFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/settings", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : undefined))
      .then((payload: { productTruth?: unknown } | undefined) => {
        if (cancelled) return;
        // Fail closed: a malformed or mismatched release payload never renders.
        const truth = sanitizePublicProductTruth(payload?.productTruth);
        if (truth) {
          setTruthEntries(truth.entries.map((e) => ({ ...e })));
        } else if (payload !== undefined) {
          setTruthFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setTruthFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const capabilities: readonly ProductSettingsCapability[] = [
    {
      id: "chat",
      label: locale === "hi" ? "Smart Shikka चैट" : "Smart Shikka Chat",
      detail: locale === "hi" ? "Canonical chat route उपलब्ध है।" : "The canonical chat route is available.",
      status: "AVAILABLE",
      truthId: "streaming-conversation",
      onOpen: () => { window.location.hash = "#chat"; },
    },
    {
      id: "skills",
      label: locale === "hi" ? "Skills और agents" : "Skills and agents",
      detail: locale === "hi" ? "साझा Skills surface उपलब्ध है।" : "The shared Skills surface is available.",
      status: "AVAILABLE",
      truthId: "agent-skills",
      onOpen: () => { window.location.hash = "#skills"; },
    },
    {
      id: "integrations",
      label: locale === "hi" ? "Integrations" : "Integrations",
      detail: locale === "hi" ? "Canonical Settings namespace उपलब्ध है।" : "The canonical Settings namespace is available.",
      status: "AVAILABLE",
      truthId: "external-app-adapters",
    },
    {
      id: "browser",
      label: locale === "hi" ? "Browser और Extension" : "Browser and Extension",
      detail: locale === "hi" ? "स्थिति canonical runtime metadata से आती है।" : "Status comes from canonical runtime metadata.",
      status: "FACTORY_PROOF_PENDING",
      truthId: "browser-control",
    },
    ...(superAdmin
      ? [
          {
            id: "admin:organization",
            label: locale === "hi" ? "संगठन और उपयोगकर्ता" : "Organization and users",
            detail: locale === "hi" ? "मौजूदा Super Admin surface खोलें।" : "Open the existing Super Admin surface.",
            status: "AVAILABLE" as const,
            truthId: "platform-internal",
            onOpen: () => { window.location.hash = "#admin"; },
          },
          {
            id: "admin:emergency-stop",
            label: locale === "hi" ? "आपातकालीन रोक" : "Emergency stop",
            detail: locale === "hi" ? "Owner-gated canonical control खोलें।" : "Open the owner-gated canonical control.",
            status: "AVAILABLE" as const,
            truthId: "approval-governance",
            onOpen: () => { window.location.hash = "#stop"; },
          },
        ]
      : []),
  ];

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0, overflow: "hidden" }}>
      <AppNav activeRoute="settings" />
      <main style={{ flex: 1, minWidth: 0, overflow: "auto", padding: 12 }}>
        {truthFailed ? (
          <SafeErrorNotice code="product_truth_unavailable" locale={locale} style={{ marginBottom: 12 }} />
        ) : null}
        <ProductSettingsSurface
          locale={locale}
          theme={theme}
          token={token ?? undefined}
          readOnly={!superAdmin}
          isSuperAdmin={superAdmin}
          capabilities={capabilities}
          truthEntries={truthEntries}
          accountInfo={account ?? undefined}
          initialSection={initialSection}
          usageSlot={(
            <CostUsagePanel
              locale={locale}
              token={token ?? undefined}
            />
          )}
          voiceSlot={(
            <>
              <VoiceSettingsPanel
                locale={locale}
                token={token ?? undefined}
              />
              <div className="ss-card" style={{ marginTop: 10 }}>
                <strong>{locale === "hi" ? "वॉइस इंजन डैशबोर्ड" : "Voice engine dashboard"}</strong>
                <p className="muted" style={{ marginTop: 4 }}>
                  {locale === "hi"
                    ? "स्थानिक voice sidecar की readiness/policy/TTS preview डेस्कटॉप ऐप में उपलब्ध है।"
                    : "Local voice sidecar readiness, policy and TTS preview are available in the desktop app."}
                </p>
              </div>
            </>
          )}
          providersSlot={(
            <ByokProvidersPanel
              locale={locale}
              token={token ?? undefined}
            />
          )}
          memorySlot={(
            <MemoryPanel
              locale={locale}
              token={token ?? undefined}
            />
          )}
          chatHistorySlot={(
            <ChatHistoryPanel
              locale={locale}
              token={token ?? undefined}
              onOpenConversation={(id) => {
                try { window.sessionStorage.setItem('ss.openConversationId', id); } catch { /* storage unavailable */ }
                window.location.hash = '#chat';
              }}
            />
          )}
          knowledgeSlot={(
            <KnowledgeLibraryPanel
              locale={locale}
              token={token ?? undefined}
            />
          )}
          skillsSlot={(
            <SkillsSettings
              service={skillsService}
              readOnly
            />
          )}
          connectionsSlot={(
            <>
              <ConnectionsPanel
                locale={locale}
                token={token ?? undefined}
              />
              <McpToolsPanel
                locale={locale}
                token={token ?? undefined}
              />
            </>
          )}
          domainsSlot={
            superAdmin ? (
              <DomainsPanel
                locale={locale}
                token={token ?? undefined}
              />
            ) : undefined
          }
          betaSlot={
            superAdmin ? (
              <BetaReadinessPanel
                locale={locale}
                token={token ?? undefined}
              />
            ) : undefined
          }
          rulesSlot={(
            <GlobalRulesPanel
              locale={locale}
              token={token ?? undefined}
              readOnly={!superAdmin}
            />
          )}
          versionSlot={<SettingsVersionDetails />}
          onLocaleChange={setLocale}
          onThemeChange={setTheme}
        />
      </main>
    </div>
  );
}
